import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useLiveQuery } from 'dexie-react-hooks';
import { scaleIngredients, scaleSteps, formatQuantity, type RecipeIngredient } from '@familyhub/shared';
import { useSession } from '../session';
import { useHoldUpdates } from '../platform/update';
import { rdb } from './db';
import { logCooked } from './actions';

/**
 * Cook mode — the recipe reduced to what you need with wet hands at the stove:
 * one big step at a time, large prev/next targets, a peek at the (servings-scaled)
 * ingredients, and the screen kept awake with the Wake Lock API so it doesn't dim
 * mid-step. The only thing it writes is the cook log, at the end, if you say you
 * cooked it (which is what feeds "what we cook a lot and what not").
 */
export function CookModeScreen() {
  const { id } = useParams<{ id: string }>();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const session = useSession();
  const userId = session.status === 'in' ? session.userId : null;
  const loaded = useLiveQuery(async () => ({ r: await rdb.recepten.get(id!) }), [id]);

  const [step, setStep] = useState(0);
  const [showIngredients, setShowIngredients] = useState(false);
  const [done, setDone] = useState(false);

  // Which step you are on is the one piece of state in this app that exists
  // nowhere but React, so a deploy applying itself here would put somebody back
  // on step 1 with a pan on the heat. It waits until cooking is over.
  useHoldUpdates();

  // Keep the screen awake for the whole session; re-acquire when the app returns
  // to the foreground (the lock is dropped on tab switch or a phone call).
  useEffect(() => {
    let sentinel: { release: () => Promise<void> } | null = null;
    let stopped = false;
    const wakeLock = (navigator as unknown as {
      wakeLock?: { request: (t: 'screen') => Promise<{ release: () => Promise<void> }> };
    }).wakeLock;
    const request = async () => {
      if (!wakeLock || stopped || document.visibilityState !== 'visible') return;
      try {
        sentinel = await wakeLock.request('screen');
      } catch {
        /* denied or unsupported — cooking still works, the screen may just dim */
      }
    };
    const onVisible = () => {
      if (document.visibilityState === 'visible') void request();
    };
    void request();
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      stopped = true;
      document.removeEventListener('visibilitychange', onVisible);
      void sentinel?.release().catch(() => {});
    };
  }, []);

  if (loaded === undefined) return <div className="cook" />;
  const recipe = loaded.r;
  if (!recipe || recipe.deleted_at) {
    return (
      <div className="cook">
        <p className="agenda-empty">Dit recept bestaat niet meer.</p>
        <Link className="btn btn-primary" to="/recepten">Terug</Link>
      </div>
    );
  }

  const steps = recipe.steps;
  if (steps.length === 0) {
    return (
      <div className="cook">
        <p className="agenda-empty">Dit recept heeft nog geen stappen.</p>
        <Link className="btn btn-primary" to={`/recepten/${recipe.id}`}>Terug</Link>
      </div>
    );
  }

  const porties = Number(params.get('porties'));
  const servings = Number.isFinite(porties) && porties > 0 ? porties : recipe.servings;
  const scaled = scaleIngredients(recipe.ingredients, recipe.servings, servings);
  const scaledSteps = scaleSteps(recipe.steps, recipe.ingredients, recipe.servings, servings);

  const backToRecipe = `/recepten/${recipe.id}?porties=${servings}`;

  // At the last step, "Klaar" opens the completion prompt: logging a cook is the
  // one write cook mode makes, and only on an explicit "yes".
  if (done) {
    return (
      <div className="cook">
        <div className="cook-top">
          <Link className="btn btn-quiet" to={backToRecipe} aria-label="Kookstand sluiten">✕</Link>
          <span className="cook-progress">Klaar</span>
          <span style={{ width: 44 }} aria-hidden />
        </div>
        <div className="cook-done">
          <h2>Heb je dit gekookt?</h2>
          <div className="cook-done-actions">
            <button
              className="btn btn-primary btn-lg"
              onClick={async () => {
                await logCooked(recipe.id, userId);
                navigate(backToRecipe);
              }}
            >
              Ja, gekookt vandaag
            </button>
            <button className="btn btn-quiet btn-lg" onClick={() => navigate(backToRecipe)}>
              Nee, alleen bekeken
            </button>
          </div>
        </div>
      </div>
    );
  }

  const atEnd = step >= steps.length - 1;

  return (
    <div className="cook">
      <div className="cook-top">
        <Link className="btn btn-quiet" to={`/recepten/${recipe.id}?porties=${servings}`} aria-label="Kookstand sluiten">✕</Link>
        <span className="cook-progress">Stap {step + 1} / {steps.length}</span>
        <button className="btn btn-quiet" onClick={() => setShowIngredients((s) => !s)} aria-pressed={showIngredients}>
          Ingrediënten
        </button>
      </div>

      {showIngredients && (
        <ul className="cook-ingredients">
          {scaled.map((ing, i) => (
            <li key={i}>
              <span className="ing-amt">{amountLabel(ing)}</span>
              <span className="ing-name">{ing.name}</span>
            </li>
          ))}
        </ul>
      )}

      <div className="cook-step">
        <span className="cook-step-num">{step + 1}</span>
        <p className="cook-step-text">{scaledSteps[step]!.text}</p>
      </div>

      <div className="cook-nav">
        <button className="btn btn-quiet btn-lg" onClick={() => setStep((s) => Math.max(0, s - 1))} disabled={step === 0}>
          Vorige
        </button>
        {atEnd ? (
          <button className="btn btn-primary btn-lg" onClick={() => setDone(true)}>Klaar</button>
        ) : (
          <button className="btn btn-primary btn-lg" onClick={() => setStep((s) => Math.min(steps.length - 1, s + 1))}>
            Volgende
          </button>
        )}
      </div>
    </div>
  );
}

function amountLabel(ing: RecipeIngredient): string {
  if (ing.quantity == null) return '';
  return ing.unit ? `${formatQuantity(ing.quantity)} ${ing.unit}` : formatQuantity(ing.quantity);
}
